import React, { useState } from 'react';
import { Plus, Trash2, Edit, GripVertical, X, Save, AlertCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';

const OpportunityStagesEditor = ({ config, onChange }) => {
  const [editingStage, setEditingStage] = useState(null);
  const [draggedIndex, setDraggedIndex] = useState(null);

  const kanbanStages = config.kanbanStages?.opportunity || [];
  const mapping = config.opportunityStageMapping || {};

  // Get all stage values referenced in mappings
  const getReferencedValues = () => {
    const referenced = new Set();
    Object.values(mapping).forEach(values => {
      if (Array.isArray(values)) {
        values.forEach(v => referenced.add(v));
      }
    });
    return referenced;
  };

  const referencedValues = getReferencedValues();

  const handleAddStage = () => {
    const newStage = {
      value: `stage_${Date.now()}`,
      label: 'New Stage',
      labelTH: 'ขั้นตอนใหม่',
      order: kanbanStages.length + 1
    };
    setEditingStage(newStage);
  };

  const handleEditStage = (stage) => {
    setEditingStage({ ...stage });
  };

  const handleSaveStage = () => {
    if (!editingStage.value || !editingStage.label) {
      toast.error('Stage value and label are required');
      return;
    }

    const updatedStages = [...kanbanStages];
    const existingIndex = updatedStages.findIndex(s => s.value === editingStage.value);

    if (existingIndex >= 0) {
      updatedStages[existingIndex] = editingStage;
    } else {
      updatedStages.push(editingStage);
    }

    // Reorder stages
    updatedStages.forEach((stage, index) => {
      stage.order = index + 1;
    });

    onChange({
      ...config,
      kanbanStages: {
        ...(config.kanbanStages || {}),
        opportunity: updatedStages
      }
    });

    setEditingStage(null);
    toast.success('Opportunity stage saved successfully');
  };

  const handleDeleteStage = (stageValue) => {
    // Check if stage is referenced in mappings
    if (referencedValues.has(stageValue)) {
      toast.error(`Cannot delete stage "${stageValue}" because it is referenced in stage mappings`);
      return;
    }

    if (window.confirm(`Are you sure you want to delete opportunity stage "${stageValue}"?`)) {
      const updatedStages = kanbanStages.filter(s => s.value !== stageValue);
      
      // Reorder remaining stages
      updatedStages.forEach((stage, index) => {
        stage.order = index + 1;
      });

      onChange({
        ...config,
        kanbanStages: {
          ...(config.kanbanStages || {}),
          opportunity: updatedStages
        }
      });

      toast.success('Opportunity stage deleted successfully');
    }
  };

  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const updatedStages = [...kanbanStages];
    const draggedStage = updatedStages[draggedIndex];
    updatedStages.splice(draggedIndex, 1);
    updatedStages.splice(index, 0, draggedStage);

    // Reorder stages
    updatedStages.forEach((stage, idx) => {
      stage.order = idx + 1;
    });

    onChange({
      ...config,
      kanbanStages: {
        ...(config.kanbanStages || {}),
        opportunity: updatedStages
      }
    });

    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
            Opportunity Stages
          </h2>
          <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
            Configure the available stage values for opportunities. These values will appear in opportunity forms and can be mapped to pipeline stages.
          </p>
        </div>
        <button
          onClick={handleAddStage}
          className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
        >
          <Plus className="h-4 w-4" />
          Add Stage
        </button>
      </div>

      {/* Stages List */}
      <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm">
        <div className="divide-y divide-slate-200 dark:divide-slate-700">
          {kanbanStages.length === 0 ? (
            <div className="p-8 text-center text-slate-500 dark:text-slate-400">
              No opportunity stages configured. Click "Add Stage" to create one.
            </div>
          ) : (
            kanbanStages.map((stage, index) => {
              const isReferenced = referencedValues.has(stage.value);
              
              return (
                <div
                  key={stage.value}
                  draggable
                  onDragStart={() => handleDragStart(index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragEnd={handleDragEnd}
                  className={`p-4 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors flex items-center gap-4 ${
                    draggedIndex === index ? 'opacity-50' : ''
                  }`}
                >
                  <GripVertical className="h-5 w-5 text-slate-400 cursor-move" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-slate-900 dark:text-slate-100">
                        {stage.label}
                      </span>
                      {stage.labelTH && (
                        <span className="text-sm text-slate-500 dark:text-slate-400">
                          ({stage.labelTH})
                        </span>
                      )}
                      {isReferenced && (
                        <span className="text-xs px-2 py-0.5 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded">
                          Mapped
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-slate-500 dark:text-slate-400">
                      Value: {stage.value} | Order: {stage.order}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleEditStage(stage)}
                      className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                      title="Edit"
                    >
                      <Edit className="h-4 w-4 text-slate-600 dark:text-slate-400" />
                    </button>
                    <button
                      onClick={() => handleDeleteStage(stage.value)}
                      disabled={isReferenced}
                      className="p-2 rounded-lg hover:bg-rose-100 dark:hover:bg-rose-900/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title={isReferenced ? 'Cannot delete: referenced in mappings' : 'Delete'}
                    >
                      <Trash2 className="h-4 w-4 text-rose-600 dark:text-rose-400" />
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {referencedValues.size > 0 && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800 dark:text-blue-300">
              <p className="font-medium mb-1">Note:</p>
              <p>
                Stages marked as "Mapped" are referenced in stage mappings and cannot be deleted. 
                Remove them from stage mappings first if you want to delete them.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingStage && (
        <StageEditModal
          stage={editingStage}
          onSave={handleSaveStage}
          onCancel={() => setEditingStage(null)}
          onChange={(updatedStage) => setEditingStage(updatedStage)}
        />
      )}
    </div>
  );
};

const StageEditModal = ({ stage, onSave, onCancel, onChange }) => {
  const [localStage, setLocalStage] = useState(stage);

  React.useEffect(() => {
    setLocalStage(stage);
  }, [stage]);

  const handleChange = (field, value) => {
    const updated = { ...localStage, [field]: value };
    setLocalStage(updated);
    onChange(updated);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-2xl w-full mx-4">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
              {stage.value ? 'Edit Opportunity Stage' : 'Add Opportunity Stage'}
            </h3>
            <button
              onClick={onCancel}
              className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Stage Value *
              </label>
              <input
                type="text"
                value={localStage.value || ''}
                onChange={(e) => handleChange('value', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                placeholder="e.g., Prospecting, Closed Won"
                disabled={!!stage.value}
              />
              <p className="text-xs text-slate-500 mt-1">
                Unique identifier for this stage. Cannot be changed after creation.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Label (EN) *
                </label>
                <input
                  type="text"
                  value={localStage.label || ''}
                  onChange={(e) => handleChange('label', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                  placeholder="e.g., Prospecting"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Label (TH)
                </label>
                <input
                  type="text"
                  value={localStage.labelTH || ''}
                  onChange={(e) => handleChange('labelTH', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                  placeholder="e.g., ค้นหาโอกาส"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-6">
            <button
              onClick={onCancel}
              className="px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={onSave}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2"
            >
              <Save className="h-4 w-4" />
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OpportunityStagesEditor;

